import React, { useEffect, useMemo, useState } from "react";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { exportTableToCsv } from "../../utils/exportTableCsv";
import { getDashboard } from "../../redux/actions/dashboardAction";
import "./dashboard.scss";

const MONTHS = [
    { label: "All months", value: 0 },
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
];

const CHANNEL_META = {
    ios: { icon: "pi pi-apple", shortLabel: "iOS IAP", color: "#7c3aed" },
    android: { icon: "pi pi-android", shortLabel: "Android", color: "#10b981" },
    web: { icon: "pi pi-globe", shortLabel: "Web", color: "#2563eb" },
};

const EMPTY_LIST = [];
const PLAN_COLORS = ["#f59e0b", "#7c3aed", "#2563eb", "#10b981", "#ec4899", "#0891b2"];
const BRAND_YELLOW = "#f7b500";
const SOURCE_LABELS = {
    subscription: "Subscription",
    landing_shop: "Web shop",
    app_product: "App product",
    search_pro: "Search Pro",
};
const PURCHASER_SEARCH_FIELDS = [
    "name",
    "email",
    "phone",
    "userCode",
    "planName",
    "channel",
    "source",
    "reference",
];

const currency = (value, maximumFractionDigits = 0) =>
    new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits,
    }).format(Number(value) || 0);

const number = (value) => new Intl.NumberFormat("en-IN").format(Number(value) || 0);

const formatRangeDate = (value) =>
    new Intl.DateTimeFormat("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
    }).format(new Date(`${value}T00:00:00`));

const formatPurchaseDate = (value) =>
    value
        ? new Intl.DateTimeFormat("en-IN", {
              dateStyle: "medium",
              timeStyle: "short",
              timeZone: "Asia/Kolkata",
          }).format(new Date(value))
        : "—";

const MetricCard = ({ label, value, helper, icon, tone = "violet", onClick }) => (
    <button type="button" className={`dashboard-metric dashboard-metric--${tone}`} onClick={onClick}>
        <span className="dashboard-metric__icon">
            <i className={icon} />
        </span>
        <span className="dashboard-metric__content">
            <span className="dashboard-metric__label">{label}</span>
            <strong>{value}</strong>
            <small>{helper}</small>
        </span>
    </button>
);

const Dashboard = ({ revenueOnly = false }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const dashboardData = useSelector((state) => state.dashboard.dashboardData);
    const currentYear = new Date().getFullYear();
    const [filters, setFilters] = useState({
        year: currentYear,
        month: 0,
        startDate: "",
        endDate: "",
        channel: "all",
        planKey: "all",
    });
    const [purchaserSearch, setPurchaserSearch] = useState("");
    const [purchaserFirst, setPurchaserFirst] = useState(0);
    const [purchaserRows, setPurchaserRows] = useState(10);
    const [purchaserSort, setPurchaserSort] = useState({
        field: "purchasedAt",
        order: -1,
    });

    useEffect(() => {
        if (Boolean(filters.startDate) !== Boolean(filters.endDate)) {
            return;
        }
        dispatch(getDashboard(filters));
    }, [dispatch, filters]);

    const updateFilter = (name, value) => {
        setFilters((previous) => ({
            ...previous,
            [name]: name === "year" || name === "month" ? Number(value) : value,
        }));
    };

    const updateDateFilter = (name, value) => {
        setFilters((previous) => {
            if (!value) {
                return { ...previous, startDate: "", endDate: "" };
            }

            const next = { ...previous, [name]: value, month: 0 };
            if (name === "startDate" && next.endDate && value > next.endDate) {
                next.endDate = "";
            }
            if (name === "endDate" && next.startDate && value < next.startDate) {
                next.startDate = "";
            }
            return next;
        });
    };

    const resetFilters = () => {
        setFilters({
            year: currentYear,
            month: 0,
            startDate: "",
            endDate: "",
            channel: "all",
            planKey: "all",
        });
    };

    const availableYears = useMemo(() => {
        const years = dashboardData?.filters?.availableYears || dashboardData?.years || [];
        return Array.from(new Set([currentYear, ...years])).sort((a, b) => b - a);
    }, [dashboardData, currentYear]);

    const summary = dashboardData.summary || {};
    const revenueSeries = dashboardData.revenue || EMPTY_LIST;
    const channelBreakdown = dashboardData.channelBreakdown || EMPTY_LIST;
    const planBreakdown = dashboardData.planBreakdown || EMPTY_LIST;
    const purchasers = dashboardData.purchasers || EMPTY_LIST;
    const planOptions = dashboardData.filters?.planOptions || EMPTY_LIST;

    useEffect(() => {
        setPurchaserFirst(0);
    }, [purchasers]);

    const selectedMonthLabel = MONTHS.find((item) => item.value === filters.month)?.label;
    const hasCustomDateRange = Boolean(filters.startDate && filters.endDate);
    const isSameMonthDateRange =
        hasCustomDateRange && filters.startDate.slice(0, 7) === filters.endDate.slice(0, 7);
    const isDailyRevenueSeries = Boolean(filters.month) || isSameMonthDateRange;
    const periodLabel = hasCustomDateRange
        ? `${formatRangeDate(filters.startDate)} – ${formatRangeDate(filters.endDate)}`
        : filters.month
          ? `${selectedMonthLabel} ${filters.year}`
          : `January – December ${filters.year}`;

    const filteredPurchasers = useMemo(() => {
        const search = purchaserSearch.trim().toLowerCase();
        if (!search) {
            return purchasers;
        }

        return purchasers.filter((item) =>
            PURCHASER_SEARCH_FIELDS.some((field) =>
                String(item[field] ?? "").toLowerCase().includes(search)
            )
        );
    }, [purchasers, purchaserSearch]);

    const sortedPurchasers = useMemo(() => {
        const rows = [...filteredPurchasers];
        const { field, order } = purchaserSort;

        rows.sort((left, right) => {
            let leftValue = left[field];
            let rightValue = right[field];

            if (field === "purchasedAt") {
                leftValue = leftValue ? new Date(leftValue).getTime() : 0;
                rightValue = rightValue ? new Date(rightValue).getTime() : 0;
            }

            if (typeof leftValue === "number" || typeof rightValue === "number") {
                return ((Number(leftValue) || 0) - (Number(rightValue) || 0)) * order;
            }

            return (
                String(leftValue ?? "").localeCompare(String(rightValue ?? ""), "en", {
                    numeric: true,
                    sensitivity: "base",
                }) * order
            );
        });

        return rows;
    }, [filteredPurchasers, purchaserSort]);

    const visiblePurchasers = sortedPurchasers.slice(
        purchaserFirst,
        purchaserFirst + purchaserRows
    );

    const exportVisiblePurchasers = () => {
        // Exports every purchaser matching the current search/filter (not just
        // the visible page) — sortedPurchasers, not visiblePurchasers.
        exportTableToCsv(
            `revenue-purchasers-${new Date().toISOString().slice(0, 10)}.csv`,
            sortedPurchasers,
            [
                { header: "Purchase Date", value: (item) => formatPurchaseDate(item.purchasedAt) },
                { header: "Name", value: (item) => item.name || "Unknown user" },
                { header: "Phone", value: (item) => item.phone || "" },
                { header: "User ID", value: (item) => item.userCode || "" },
                { header: "Plan", value: (item) => item.planName || "Plan" },
                { header: "Plan Amount (INR)", value: (item) => Number(item.planAmount) || 0 },
                { header: "Channel", value: (item) => CHANNEL_META[item.channel]?.shortLabel || item.channel || "Unknown" },
                { header: "Amount Paid (INR)", value: (item) => Number(item.amountPaid) || 0 },
                { header: "Purchase Type", value: (item) => SOURCE_LABELS[item.source] || item.source || "" },
                { header: "Reference", value: (item) => item.reference || "" },
            ]
        );
    };

    const purchaserTemplate = (item) => (
        <div className="dashboard-purchaser-user">
            <strong>{item.name || "Unknown user"}</strong>
            <small>{item.email || item.userCode || "No email available"}</small>
        </div>
    );

    const planTemplate = (item) => (
        <div className="dashboard-purchaser-user">
            <strong>{item.planName || "Plan"}</strong>
            <small>{currency(item.planAmount)}</small>
        </div>
    );

    const channelTemplate = (item) => {
        const meta = CHANNEL_META[item.channel] || {};
        return (
            <span className="dashboard-purchaser-channel">
                <i className={meta.icon || "pi pi-credit-card"} />
                {meta.shortLabel || item.channel || "Unknown"}
            </span>
        );
    };

    const revenueChartData = useMemo(
        () => ({
            labels: revenueSeries.map((item) => item.label || item.monthName),
            datasets: [
                {
                    label: "Revenue",
                    data: revenueSeries.map((item) => item.totalRevenue),
                    borderColor: BRAND_YELLOW,
                    backgroundColor: "rgba(247, 181, 0, 0.17)",
                    pointBackgroundColor: "#ffffff",
                    pointBorderColor: BRAND_YELLOW,
                    pointBorderWidth: 2,
                    pointRadius: revenueSeries.length > 20 ? 2 : 4,
                    pointHoverRadius: 6,
                    borderWidth: 3,
                    fill: true,
                    tension: 0.38,
                },
            ],
        }),
        [revenueSeries]
    );

    const revenueChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: { intersect: false, mode: "index" },
            plugins: {
                legend: { display: false },
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        label: (context) => `Revenue: ${currency(context.parsed.y)}`,
                        afterLabel: (context) => {
                            const purchases = revenueSeries[context.dataIndex]?.count || 0;
                            return `${number(purchases)} purchase${purchases === 1 ? "" : "s"}`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: {
                        color: "#64748b",
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: isSameMonthDateRange ? 31 : filters.month ? 16 : 12,
                    },
                    border: { display: false },
                },
                y: {
                    beginAtZero: true,
                    grid: { color: "rgba(247, 181, 0, 0.14)", drawBorder: false },
                    border: { display: false },
                    ticks: {
                        color: "#64748b",
                        callback: (value) => (value >= 1000 ? `₹${value / 1000}k` : `₹${value}`),
                    },
                },
            },
        }),
        [filters.month, isSameMonthDateRange, revenueSeries]
    );

    const channelChartData = useMemo(
        () => ({
            labels: channelBreakdown.map((item) => CHANNEL_META[item.channel]?.shortLabel || item.label),
            datasets: [
                {
                    data: channelBreakdown.map((item) => item.totalRevenue),
                    backgroundColor: channelBreakdown.map((item) => CHANNEL_META[item.channel]?.color || "#94a3b8"),
                    borderColor: "#ffffff",
                    borderWidth: 5,
                    hoverOffset: 8,
                },
            ],
        }),
        [channelBreakdown]
    );

    const channelChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            cutout: "72%",
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (context) => `${context.label}: ${currency(context.parsed)}`,
                    },
                },
            },
        }),
        []
    );

    const visiblePlans = planBreakdown;
    const planChartHeight = Math.max(320, visiblePlans.length * 70);
    const planChartData = useMemo(
        () => ({
            labels: visiblePlans.map((item) => `${item.label}${item.isDeactivated ? " (old)" : ""}`),
            datasets: [
                {
                    label: "Revenue",
                    data: visiblePlans.map((item) => item.totalRevenue),
                    backgroundColor: visiblePlans.map((_, index) => PLAN_COLORS[index % PLAN_COLORS.length]),
                    borderRadius: 9,
                    borderSkipped: false,
                    barThickness: 18,
                    categoryPercentage: 0.56,
                    barPercentage: 0.64,
                },
            ],
        }),
        [visiblePlans]
    );

    const planChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            layout: {
                padding: { top: 12, right: 8, bottom: 12, left: 8 },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (context) => ` ${currency(context.parsed.x)}`,
                    },
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    grid: { color: "rgba(148, 163, 184, 0.18)" },
                    border: { display: false },
                    ticks: { color: "#64748b", callback: (value) => currency(value) },
                },
                y: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: "#334155", padding: 10, font: { weight: "600" } },
                },
            },
        }),
        []
    );

    if (!revenueOnly) {
        return (
            <main className="dashboard-modern">
                <header className="dashboard-heading">
                    <div>
                        <span className="dashboard-eyebrow">BUSINESS OVERVIEW</span>
                        <h1>Dashboard</h1>
                        <p>Live platform activity at a glance.</p>
                    </div>
                    <div className="dashboard-live-status"><span /> Live data</div>
                </header>
                <section className="dashboard-metrics dashboard-metrics--operations" aria-label="Operational summary">
                    <MetricCard
                        label="Active users"
                        value={number(dashboardData.activeUsers)}
                        helper="Currently active users"
                        icon="pi pi-users"
                        tone="green"
                        onClick={() => navigate("/user")}
                    />
                    <MetricCard
                        label="Active QRs"
                        value={number(dashboardData.activeQrCodes)}
                        helper="QR codes in use"
                        icon="pi pi-qrcode"
                        tone="blue"
                        onClick={() => navigate("/qr-code")}
                    />
                    <MetricCard
                        label="Replacement orders"
                        value={number(dashboardData.totalReplacementOrders)}
                        helper="Total replacement requests"
                        icon="pi pi-replay"
                        tone="amber"
                        onClick={() => navigate("/replacement")}
                    />
                    <MetricCard
                        label="Shipping orders"
                        value={number(dashboardData.totalShippingOrders)}
                        helper="Active shipping orders"
                        icon="pi pi-shopping-bag"
                        tone="violet"
                        onClick={() => navigate("/shipping-order")}
                    />
                    <MetricCard
                        label="New QRs"
                        value={number(dashboardData.newQrCodes)}
                        helper="Recently generated QR codes"
                        icon="pi pi-plus-circle"
                        tone="green"
                        onClick={() => navigate("/qr-code")}
                    />
                </section>
            </main>
        );
    }

    return (
        <main className="dashboard-modern">
            <header className="dashboard-heading">
                <div>
                    <span className="dashboard-eyebrow">BUSINESS OVERVIEW</span>
                    <h1>Revenue dashboard</h1>
                    <p>Track plan performance across every sales channel.</p>
                </div>
                <div className="dashboard-live-status">
                    <span />
                    Live data
                </div>
            </header>

            <section className="dashboard-filters" aria-label="Dashboard filters">
                <div className="dashboard-filter">
                    <label htmlFor="dashboard-year">Year</label>
                    <select
                        id="dashboard-year"
                        value={filters.year}
                        disabled={hasCustomDateRange}
                        onChange={(event) => updateFilter("year", event.target.value)}
                    >
                        {availableYears.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="dashboard-filter">
                    <label htmlFor="dashboard-month">Month</label>
                    <select
                        id="dashboard-month"
                        value={filters.month}
                        disabled={hasCustomDateRange}
                        onChange={(event) => updateFilter("month", event.target.value)}
                    >
                        {MONTHS.map((month) => (
                            <option key={month.value} value={month.value}>
                                {month.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="dashboard-filter">
                    <label htmlFor="dashboard-start-date">Start date</label>
                    <input
                        id="dashboard-start-date"
                        type="date"
                        value={filters.startDate}
                        max={filters.endDate || undefined}
                        onChange={(event) => updateDateFilter("startDate", event.target.value)}
                    />
                </div>
                <div className="dashboard-filter">
                    <label htmlFor="dashboard-end-date">End date</label>
                    <input
                        id="dashboard-end-date"
                        type="date"
                        value={filters.endDate}
                        min={filters.startDate || undefined}
                        onChange={(event) => updateDateFilter("endDate", event.target.value)}
                    />
                </div>
                <div className="dashboard-filter">
                    <label htmlFor="dashboard-channel">Purchase channel</label>
                    <select
                        id="dashboard-channel"
                        value={filters.channel}
                        onChange={(event) => updateFilter("channel", event.target.value)}
                    >
                        <option value="all">All channels</option>
                        <option value="ios">iOS in-app purchase</option>
                        <option value="android">Android Razorpay</option>
                        <option value="web">Web Razorpay</option>
                    </select>
                </div>
                <div className="dashboard-filter">
                    <label htmlFor="dashboard-plan">Subscription plan</label>
                    <select
                        id="dashboard-plan"
                        value={filters.planKey}
                        onChange={(event) => updateFilter("planKey", event.target.value)}
                    >
                        <option value="all">All plans</option>
                        {planOptions.map((plan) => (
                            <option key={plan.value} value={plan.value}>
                                {plan.label}
                            </option>
                        ))}
                    </select>
                </div>
                <button type="button" className="dashboard-reset" onClick={resetFilters}>
                    <i className="pi pi-refresh" />
                    Reset
                </button>
            </section>

            <section className="dashboard-metrics" aria-label="Revenue summary">
                <MetricCard
                    label="Total revenue"
                    value={currency(summary.totalRevenue)}
                    helper={periodLabel}
                    icon="pi pi-wallet"
                    tone="violet"
                />
                <MetricCard
                    label="Purchases"
                    value={number(summary.totalPurchases)}
                    helper="Successful paid purchases"
                    icon="pi pi-shopping-cart"
                    tone="blue"
                />
                <MetricCard
                    label="Active users"
                    value={number(dashboardData.activeUsers)}
                    helper={`${number(dashboardData.activeQrCodes)} active QR codes`}
                    icon="pi pi-users"
                    tone="green"
                    onClick={() => navigate("/user")}
                />
            </section>

            <section className="dashboard-chart-grid">
                <article className="dashboard-panel dashboard-panel--trend">
                    <div className="dashboard-panel__header">
                        <div>
                            <span className="dashboard-panel__eyebrow">REVENUE TREND</span>
                            <h2>{isDailyRevenueSeries ? "Daily revenue" : "Monthly revenue"}</h2>
                            <p>{periodLabel}</p>
                        </div>
                        <span className="dashboard-total-pill">{currency(summary.totalRevenue)}</span>
                    </div>
                    <div className="dashboard-chart dashboard-chart--line">
                        <Chart type="line" data={revenueChartData} options={revenueChartOptions} />
                    </div>
                </article>

                <article className="dashboard-panel dashboard-panel--channels">
                    <div className="dashboard-panel__header">
                        <div>
                            <span className="dashboard-panel__eyebrow">PAYMENT MIX</span>
                            <h2>Revenue by channel</h2>
                            <p>Successful purchase sources</p>
                        </div>
                    </div>
                    <div className="dashboard-doughnut-wrap">
                        <div className="dashboard-chart dashboard-chart--doughnut">
                            <Chart type="doughnut" data={channelChartData} options={channelChartOptions} />
                            <div className="dashboard-doughnut-center">
                                <strong>{number(summary.totalPurchases)}</strong>
                                <span>purchases</span>
                            </div>
                        </div>
                    </div>
                    <div className="dashboard-channel-list">
                        {channelBreakdown.map((item) => {
                            const meta = CHANNEL_META[item.channel] || {};
                            return (
                                <button type="button" key={item.channel} onClick={() => updateFilter("channel", item.channel)}>
                                    <span className="dashboard-channel-icon" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
                                        <i className={meta.icon} />
                                    </span>
                                    <span>
                                        <strong>{meta.shortLabel || item.label}</strong>
                                        <small>{number(item.count)} purchases</small>
                                    </span>
                                    <b>{currency(item.totalRevenue)}</b>
                                </button>
                            );
                        })}
                    </div>
                    <p className="dashboard-data-note">
                        <i className="pi pi-info-circle" />
                        Historical subscription Razorpay payments without a saved source are included in Android.
                    </p>
                </article>
            </section>

            <section className="dashboard-bottom-grid">
                <article className="dashboard-panel">
                    <div className="dashboard-panel__header">
                        <div>
                            <span className="dashboard-panel__eyebrow">PLAN PERFORMANCE</span>
                            <h2>Plan revenue</h2>
                            <p>Compare plan sales and earnings</p>
                        </div>
                    </div>
                    <div className="dashboard-chart dashboard-chart--plans" style={{ height: `${planChartHeight}px` }}>
                        <Chart type="bar" data={planChartData} options={planChartOptions} />
                    </div>
                    <div className="dashboard-plan-stats">
                        {visiblePlans.map((item) => (
                            <button
                                type="button"
                                key={item.planKey}
                                onClick={() => updateFilter("planKey", item.planKey)}
                            >
                                <span>
                                    {item.label}
                                    {item.isDeactivated ? <em>Deactivated</em> : null}
                                </span>
                                <strong>{number(item.count)} sold</strong>
                                <b>{currency(item.totalRevenue)}</b>
                            </button>
                        ))}
                    </div>
                </article>

                <article className="dashboard-panel dashboard-operations">
                    <div className="dashboard-panel__header">
                        <div>
                            <span className="dashboard-panel__eyebrow">OPERATIONS</span>
                            <h2>At a glance</h2>
                            <p>Live platform activity</p>
                        </div>
                    </div>
                    <div className="dashboard-operations__grid">
                        <button type="button" onClick={() => navigate("/qr-code")}>
                            <span><i className="pi pi-qrcode" /></span>
                            <strong>{number(dashboardData.totalQrCodes)}</strong>
                            <small>Total QR codes</small>
                        </button>
                        <button type="button" onClick={() => navigate("/shipping-order")}>
                            <span><i className="pi pi-shopping-bag" /></span>
                            <strong>{number(dashboardData.totalShippingOrders)}</strong>
                            <small>Shipping orders</small>
                        </button>
                        <button type="button" onClick={() => navigate("/replacement")}>
                            <span><i className="pi pi-replay" /></span>
                            <strong>{number(dashboardData.totalReplacementOrders)}</strong>
                            <small>Replacements</small>
                        </button>
                    </div>
                </article>
            </section>

            <section className="dashboard-panel dashboard-purchasers">
                <div className="dashboard-panel__header">
                    <div>
                        <span className="dashboard-panel__eyebrow">PURCHASE DETAILS</span>
                        <h2>Users who purchased</h2>
                        <p>
                            {number(filteredPurchasers.length)} matching purchase{filteredPurchasers.length === 1 ? "" : "s"} · {periodLabel}
                        </p>
                    </div>
                    <div className="dashboard-purchaser-actions">
                        <div className="dashboard-purchaser-search">
                            <i className="pi pi-search" />
                            <input
                                type="search"
                                value={purchaserSearch}
                                onChange={(event) => {
                                    setPurchaserSearch(event.target.value);
                                    setPurchaserFirst(0);
                                }}
                                placeholder="Search purchaser"
                                aria-label="Search purchasers"
                            />
                        </div>
                        <button
                            type="button"
                            className="dashboard-export-button"
                            onClick={exportVisiblePurchasers}
                            disabled={!sortedPurchasers.length}
                        >
                            <i className="pi pi-download" />
                            Export CSV ({sortedPurchasers.length})
                        </button>
                    </div>
                </div>
                <DataTable
                    value={sortedPurchasers}
                    dataKey="purchaseId"
                    paginator
                    first={purchaserFirst}
                    rows={purchaserRows}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    onPage={(event) => {
                        setPurchaserFirst(event.first);
                        setPurchaserRows(event.rows);
                    }}
                    emptyMessage="No purchases found for the selected filters."
                    responsiveLayout="scroll"
                    stripedRows
                    sortField={purchaserSort.field}
                    sortOrder={purchaserSort.order}
                    onSort={(event) => {
                        setPurchaserFirst(0);
                        setPurchaserSort({
                            field: event.sortField || "purchasedAt",
                            order: event.sortOrder || -1,
                        });
                    }}
                >
                    <Column
                        field="purchasedAt"
                        header="Purchase date"
                        body={(item) => formatPurchaseDate(item.purchasedAt)}
                        sortable
                    />
                    <Column header="User" body={purchaserTemplate} />
                    <Column field="phone" header="Phone" body={(item) => item.phone || "—"} />
                    <Column field="planName" header="Plan" body={planTemplate} sortable />
                    <Column field="channel" header="Channel" body={channelTemplate} sortable />
                    <Column
                        field="amountPaid"
                        header="Amount"
                        body={(item) => currency(item.amountPaid)}
                        sortable
                    />
                    <Column
                        field="source"
                        header="Purchase type"
                        body={(item) => SOURCE_LABELS[item.source] || item.source || "—"}
                        sortable
                    />
                    <Column field="reference" header="Reference" body={(item) => item.reference || "—"} />
                </DataTable>
            </section>
        </main>
    );
};

export default Dashboard;
